import axios from "axios";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import Select from "react-select";
import Modal from "../../../components/Modal/Modal";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";

const AssignLeadModal = ({ 
  showModal, 
  onClose, 
  id, 
  onSuccess,
  nextUp   
}) => {
  const [config] = useAuth();
  const [loading, setLoading] = useState(false);
  const [brokerOptions, setBrokerOptions] = useState([]);

  const [formData, setFormData] = useState({
    broker: [],
    suggested_reason: "",
    override_reason: "",
  });

  const [error, setError] = useState({});

  const suggestedReasons = [
    { value: "Existing Relationship", label: "Existing Relationship" },
    { value: "Speciality / Investment Type", label: "Speciality / Investment Type" },
    { value: "Geography", label: "Geography" },
    { value: "Other", label: "Other" },
  ];

  const fetchUsers = () => {
    axios.get(`${BASE_URL}/get-datalisting?list=users&role_id=3`, config)
      .then((res) => {
        const data = res?.data?.data || [];
        const options = data.map((el) => ({
          value: el.id,
          label: `${el.first_name} ${el.last_name}`,
        }));
        setBrokerOptions(options);

        // Pre-select Next Up if available
        if (nextUp?.user_id) {
          const nextUpOption = options.find(opt => opt.value === nextUp.user_id);
          if (nextUpOption) {
            setFormData(prev => ({
              ...prev,
              broker: [nextUp.user_id]
            }));
          }
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message);
        }
      });
  };

  useEffect(() => {
    if (showModal) {
      setFormData({
        broker: [],
        suggested_reason: "",
        override_reason: "",
      });
      setError({});
      fetchUsers();
    }
  }, [showModal, nextUp]); // Added nextUp dependency

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.broker.length === 0) {
      setError({ broker: "Please select at least one broker" });
      return;
    }
    if (!formData.suggested_reason) {
      setError({ suggested_reason: "Please select a suggested reason" });
      return;
    }

    setLoading(true);

    axios.post(`${BASE_URL}/assign-lead/${id}`, formData, config)
      .then((res) => {
        NotificationManager.success(res?.data?.message || "Lead assigned successfully");
        onSuccess?.();
        onClose();
      })
      .catch((err) => {
        if (err.response?.data?.errors) {
          setError(err.response.data.errors);
        } else if (err.response?.data?.message) {
          NotificationManager.error(err.response.data.message);
        }
      })
      .finally(() => setLoading(false));
  };

  if (!showModal) return null;

  return (
    <Modal title="Assign Lead" show={showModal} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        {/* Broker */}
        <div>
          <label className="dark-H head-4 mb-2">
            Broker <span className="red-D">*</span>
          </label>
          <Select
            isMulti
            options={brokerOptions}
            placeholder="Select Broker"
            value={brokerOptions.filter(opt => formData.broker.includes(opt.value))}
            onChange={(selected) => {
              setFormData({ 
                ...formData, 
                broker: selected ? selected.map(opt => opt.value) : [] 
              });
              setError({ ...error, broker: "" });
            }}
            className="body-N"
          />
          {error.broker && <span className="body-S red-D">{error.broker}</span>}
        </div>

        {/* Suggested Reason Dropdown */}
        <div className="mt-5">
          <label className="dark-H head-4 mb-2">
            Suggested Reason <span className="red-D">*</span>
          </label>
          <Select
            options={suggestedReasons}
            placeholder="Select Reason"
            value={suggestedReasons.find(option => option.value === formData.suggested_reason) || null}
            onChange={(option) => {
              setFormData({ ...formData, suggested_reason: option?.value || "" });
              setError({ ...error, suggested_reason: "" });
            }}
            className="body-N"
          />
          {error.suggested_reason && <span className="body-S red-D">{error.suggested_reason}</span>}
        </div>

        {/* Override Reason (Optional) */}
        <div className="mt-5">
          <label className="dark-H head-4 mb-2">Override Reason / Notes</label>
          <textarea
            className="body-N w-full border rounded-lg p-3 h-24 resize-y"
            placeholder="Additional comments (optional)..."
            value={formData.override_reason}
            onChange={(e) => setFormData({ ...formData, override_reason: e.target.value })}
          />
           {error?.override_reason && <span className="body-S red-D">{error?.override_reason}</span>}
        </div>

        <div className="flex justify-end gap-4 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 green-H"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="save-button light-L head-5 green-bg-H disabled:opacity-50"
          >
            {loading ? "Assigning..." : "Assign Lead"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AssignLeadModal;